import { CalendarDays, ExternalLink, Heart, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { SpecialBrandAction, useActiveSpecialBrandActions } from "@/hooks/useSpecialBrandActions";
import { proxyImage } from "@/lib/imageProxy";
import { cn, formatDonation } from "@/lib/utils";
import { BrandLines } from "@/components/ui/BrandLines";
import badgeAsset from "@/assets/selo-marca-do-bem.png.asset.json";

const formatEndDate = (value: string) =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(value));

function BrandLogo({ action, className }: { action: SpecialBrandAction; className?: string }) {
  const brand = action.brand;
  if (!brand) return null;
  return brand.logo_url ? (
    <img
      src={proxyImage(brand.logo_url, { w: 320 }) ?? brand.logo_url}
      alt={`Logo ${brand.name}`}
      width={220}
      height={96}
      loading="lazy"
      decoding="async"
      className={cn("max-h-24 max-w-[72%] object-contain transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transition-none", className)}
    />
  ) : (
    <Store className="h-14 w-14 text-muted-foreground" aria-hidden="true" />
  );
}

function OfficialSeal({ className }: { className?: string }) {
  return (
    <div className={cn("flex shrink-0 items-center justify-center rounded-full border-4 border-card bg-card p-1 shadow-lg", className)}>
      <img
        src={badgeAsset.url}
        alt="Selo oficial Marca do Bem"
        width={96}
        height={96}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-contain"
      />
    </div>
  );
}

function DonationValue({ action, compact = false }: { action: SpecialBrandAction; compact?: boolean }) {
  const value = action.brand?.donation_percentage;
  if (value == null || value <= 0) return null;
  return (
    <div className={cn("text-success", compact ? "text-left" : "text-center")}>
      <p className="flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground">
        <Heart className="h-3.5 w-3.5 fill-success text-success" aria-hidden="true" />
        Doação vigente
      </p>
      <p className={cn("font-display font-bold leading-none", compact ? "mt-1 text-2xl" : "mt-1.5 text-3xl")}>
        Até {formatDonation(value, action.brand?.commission_type)}
      </p>
    </div>
  );
}

function ActionDetails({ action }: { action: SpecialBrandAction }) {
  const brand = action.brand;
  if (!brand) return null;
  const destination = action.link_url || `/marca/${brand.slug}`;
  return (
    <div className="flex flex-1 flex-col p-5">
      {action.show_brand_name && <h3 className="font-display text-xl font-bold text-foreground">{brand.name}</h3>}
      {action.show_tagline && <p className={cn("flex-1 text-sm leading-relaxed text-muted-foreground", action.show_brand_name && "mt-2")}>{action.tagline}</p>}
      {action.show_end_date && action.ends_at && (
        <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          Até {formatEndDate(action.ends_at)}
        </p>
      )}
      {action.show_button && (
        <Button asChild variant="outline" className="mt-4 w-full gap-2 border-foreground/15">
          <a href={destination}>
            Conhecer ação {brand.name}
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </Button>
      )}
    </div>
  );
}

export function PremiumActionCard({ action }: { action: SpecialBrandAction }) {
  const layout = action.card_layout ?? "medallion";
  const hasDonation = action.show_donation && (action.brand?.donation_percentage ?? 0) > 0;
  const hasVisualHeader = action.show_logo || action.show_seal;
  const hasDetails = action.show_brand_name || action.show_tagline || (action.show_end_date && !!action.ends_at) || action.show_button;
  const logoAreaStyle = action.logo_background_color ? { backgroundColor: action.logo_background_color } : undefined;
  return (
    <Card className="group h-full overflow-hidden border-border/70 bg-card shadow-[0_14px_34px_-20px_hsl(var(--foreground)/0.3)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_42px_-18px_hsl(var(--foreground)/0.35)] motion-reduce:transform-none motion-reduce:transition-none">
      {action.show_brand_lines && <BrandLines />}
      <CardContent className={cn("flex h-full flex-col p-0", hasDetails && "min-h-[360px]")}>
        {layout === "medallion" && (
          <>
            {hasVisualHeader && (
              <div className={cn("relative flex h-44 items-center justify-center px-8 pb-5 pt-9", !action.logo_background_color && "bg-muted/30")} style={logoAreaStyle}>
                {action.show_logo && <BrandLogo action={action} />}
                {action.show_seal && <OfficialSeal className={cn("h-24 w-24", action.show_logo && "absolute -bottom-7 right-5")} />}
              </div>
            )}
            {hasDonation && <div className={cn("border-t px-5 pb-1", action.show_seal && hasVisualHeader ? "pt-10" : "pt-5")}><DonationValue action={action} /></div>}
          </>
        )}
        {layout === "impact_band" && (
          <>
            {hasVisualHeader && (
              <div className={cn("relative flex h-36 items-center justify-center px-8 py-6", !action.logo_background_color && "bg-muted/30")} style={logoAreaStyle}>
                {action.show_logo && <BrandLogo action={action} className="max-h-20 max-w-[65%]" />}
                {action.show_seal && <OfficialSeal className={cn("h-20 w-20", action.show_logo && "absolute right-4 top-4")} />}
              </div>
            )}
            {hasDonation && <div className="relative border-y bg-secondary/50 px-5 py-4">
              {action.show_brand_lines && <BrandLines className="absolute inset-x-0 top-0" />}
              <DonationValue action={action} compact />
            </div>}
          </>
        )}
        {layout === "side_seal" && (
          hasVisualHeader || hasDonation ? <div className={cn("grid min-h-[178px] border-b", action.show_seal && (action.show_logo || hasDonation) && "grid-cols-[1fr_104px]")}>
            {(action.show_logo || hasDonation) && <div className={cn("flex min-w-0 flex-col items-center justify-center gap-4 p-5", !action.logo_background_color && "bg-muted/30")} style={logoAreaStyle}>
              {action.show_logo && <BrandLogo action={action} className="max-h-16 max-w-full" />}
              {hasDonation && <DonationValue action={action} compact />}
            </div>}
            {action.show_seal && <div className={cn("relative flex items-center justify-center bg-card/70 p-3", (action.show_logo || hasDonation) && "border-l")}>
              {action.show_brand_lines && <BrandLines className="absolute inset-y-0 left-0 h-auto w-1 grid-cols-1 grid-rows-4" />}
              <OfficialSeal className="h-24 w-24 border-2" />
            </div>}
          </div> : null
        )}
        {hasDetails && <ActionDetails action={action} />}
      </CardContent>
    </Card>
  );
}

export function SpecialBrandActionsCarousel() {
  const { data: actions } = useActiveSpecialBrandActions();
  if (!actions?.length) return null;

  return (
    <section className="border-y bg-background py-12 md:py-16" aria-labelledby="special-actions-title">
      <div className="container">
        <div className="mx-auto mb-8 max-w-3xl text-center">
          <h2 id="special-actions-title" className="font-display text-3xl font-bold md:text-4xl">
            Marcas do Bem
          </h2>
          <p className="mt-2 text-muted-foreground">
            Conheça as lojas com ações especiais em parceria com o Shopping do Bem.
          </p>
        </div>

        <Carousel opts={{ align: "start", loop: actions.length > 3 }} className="mx-auto w-full max-w-6xl px-5 md:px-10">
          <CarouselContent className="-ml-3 md:-ml-5">
            {actions.map((action) => {
              const brand = action.brand;
              if (!brand) return null;
              return (
                <CarouselItem key={action.id} className="pl-3 sm:basis-1/2 md:pl-5 lg:basis-1/3">
                  <PremiumActionCard action={action} />
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {actions.length > 1 && (
            <>
              <CarouselPrevious className="left-0 md:-left-1" />
              <CarouselNext className="right-0 md:-right-1" />
            </>
          )}
        </Carousel>
      </div>
    </section>
  );
}